"use client";

import React, { ReactNode, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { useDataContext } from "../../app/_context";
import { usePathname } from "next/navigation";

export default function NavBarBackground({
  children,
}: {
  children: ReactNode;
}) {
  const { scrollPositionY, scrollAnimationBegin } = useDataContext();
  const pathname = usePathname();
  const isBlogRoute = pathname.includes("/blog");
  const isBlogPost = pathname.includes("/blog/post/");

  const prevScrollY = useRef(scrollPositionY);
  const [scrollingDown, setScrollingDown] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const outer = document.createElement("div");
    outer.style.cssText = "visibility:hidden;overflow:scroll;position:absolute";
    document.body.appendChild(outer);
    const inner = document.createElement("div");
    outer.appendChild(inner);
    const width = outer.offsetWidth - inner.offsetWidth;
    document.body.removeChild(outer);
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      `${width}px`,
    );
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      if (scrollPositionY < 150) {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        setScrollingDown(false);
      } else if (scrollPositionY > prevScrollY.current) {
        if (!hideTimeoutRef.current) {
          hideTimeoutRef.current = setTimeout(() => {
            setScrollingDown(true);
            hideTimeoutRef.current = null;
          }, 1200);
        }
      } else if (scrollPositionY < prevScrollY.current) {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        setScrollingDown(false);
      }

      prevScrollY.current = scrollPositionY;
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [scrollPositionY]);

  const isHomePage = pathname === "/";
  const isGlass = !isHomePage && (isBlogPost || scrollPositionY > scrollAnimationBegin);
  const navHidden = isBlogRoute && scrollingDown;

  const heightClass = pathname.includes("public") ? "h-12" : "h-14 md:h-20";

  if (!pathname.includes("widget"))
    return (
      <div
        style={{
          right: !pathname.includes("public")
            ? "var(--scrollbar-width, 0px)"
            : 0,
        }}
        className={cn(
          heightClass,
          "fixed top-0 left-0 z-100 overflow-hidden pointer-events-none",
        )}
      >
        <header
          className={cn(
            heightClass,
            "flex w-full shrink-0 items-center border-b border-b-transparent px-4 md:px-6 pointer-events-auto",
            !isHomePage && "bg-background",
            "transition-[color,background-color,border-color,translate,transform] duration-500 ease-in-out translate-y-0",
            isGlass &&
              "backdrop-blur-lg border-b dark:border-stone-700 border-b-stone-300 dark:bg-[#1e262fCD] bg-white/60",
            navHidden && "-translate-y-full",
          )}
        >
          <div
            className={cn(
              "mx-auto w-full flex items-center",
              pathname.includes("public") ? null : "max-w-7xl",
            )}
          >
            {children}
          </div>
        </header>
      </div>
    );
  else return <div className="hidden">{children}</div>;
}
